import React, { useState, useCallback, useEffect } from 'react';
import { kpiApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Network,
  Wifi,
  Cable,
  Server,
  Layers,
  MessageSquare
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip } from '../../components/common/ChartTooltips';

/**
 * NetworkingDashboardPage - KPI overview for networking metrics
 */
const NetworkingDashboardPage = () => {
  const navigate = useDbNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);

  const loadKpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await kpiApi.getNetworking();
      setKpis(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when database selection changes
  useEffect(() => {
    const handler = () => loadKpis();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadKpis]);

  // Status colors
  const statusColors = {
    'Active': '#22c55e',
    'Spare': '#94a3b8',
    'Pending Connection': '#f59e0b',
    'Reserved': '#3b82f6',
    'Future': '#8b5cf6',
    'Inactive': '#ef4444',
    'De-Activated': '#ef4444',
    'Unknown': '#e2e8f0'
  };

  const getStatusColor = (status) => statusColors[status] || statusColors['Unknown'];

  // Convert API data to recharts format
  const toChartData = (grouped) => {
    if (!grouped || grouped.length === 0) return [];
    return grouped.filter(g => g.count > 0).map(g => ({
      name: g.key,
      value: g.count,
      fill: getStatusColor(g.key)
    }));
  };

  // Network section content (rendered inside DashboardTile)
  const NetworkContent = ({ total, distinctLabel, distinctValue, extraLabel, extraValue, statusData }) => {
    const chartData = toChartData(statusData);
    const totalRecords = chartData.reduce((sum, d) => sum + d.value, 0);

    return (
      <div className="network-section-content">
        <div className="network-stats">
          <div className="network-stat">
            <span className="network-stat-value">{total}</span>
            <span className="network-stat-label">Total Records</span>
          </div>
          <div className="network-stat">
            <span className="network-stat-value">{distinctValue}</span>
            <span className="network-stat-label">{distinctLabel}</span>
          </div>
          {extraLabel && (
            <div className="network-stat">
              <span className="network-stat-value">{extraValue}</span>
              <span className="network-stat-label">{extraLabel}</span>
            </div>
          )}
        </div>

        <div className="network-chart-area">
          {chartData.length > 0 ? (
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={38}
                  outerRadius={62}
                  paddingAngle={1}
                  animationDuration={600}
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="recharts-donut-label">
                  {totalRecords.toLocaleString()}
                </text>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="donut-chart empty" style={{ width: 120, height: 120 }}>
              <span className="donut-center" style={{ width: 88, height: 88 }}>0</span>
            </div>
          )}
        </div>

        <div className="network-legend">
          {statusData?.map((g, i) => (
            <span key={i} className="legend-item">
              <span className="legend-dot" style={{ backgroundColor: getStatusColor(g.key) }} />
              <span className="legend-key">{g.key}</span>
              <span className="legend-count">{g.count.toLocaleString()}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout
      id="networking"
      title="Networking Dashboard"
      icon={Network}
      onRefresh={loadKpis}
      loading={loading}
      error={error}
    >
      {/* Row 1: Summary Bar - full width */}
      <DashboardTile colSpan={6} className="summary-bar-tile">
        <div className="network-summary-bar">
          <div className="summary-stat">
            <Wifi size={18} />
            <span className="summary-value">{((kpis?.slnTotal || 0) + (kpis?.dlnTotal || 0)).toLocaleString()}</span>
            <span className="summary-label">IP Addresses</span>
          </div>
          <div className="summary-stat">
            <Layers size={18} />
            <span className="summary-value">{(kpis?.slnSubnets || 0) + (kpis?.dlnSubnets || 0)}</span>
            <span className="summary-label">Subnets</span>
          </div>
          <div className="summary-stat">
            <Cable size={18} />
            <span className="summary-value">{(kpis?.serialTotal || 0).toLocaleString()}</span>
            <span className="summary-label">Serial Connections</span>
          </div>
          <div className="summary-stat">
            <Server size={18} />
            <span className="summary-value">{kpis?.serialNetworks || 0}</span>
            <span className="summary-label">Networks</span>
          </div>
        </div>
      </DashboardTile>

      {/* Row 2: Three network tiles */}
      <DashboardTile
        title="Site Level Network (SLN)"
        icon={Wifi}
        onClick={() => navigate('/ip-address-sln')}
      >
        <NetworkContent
          total={kpis?.slnTotal || 0}
          distinctLabel="Subnets"
          distinctValue={kpis?.slnSubnets || 0}
          statusData={kpis?.slnByStatus}
        />
      </DashboardTile>

      <DashboardTile
        title="Device Level Network (DLN)"
        icon={Server}
        onClick={() => navigate('/ip-address-dln')}
      >
        <NetworkContent
          total={kpis?.dlnTotal || 0}
          distinctLabel="Subnets"
          distinctValue={kpis?.dlnSubnets || 0}
          statusData={kpis?.dlnByStatus}
        />
      </DashboardTile>

      <DashboardTile
        title="Serial Networks"
        icon={Cable}
        onClick={() => navigate('/networks-serial')}
      >
        <NetworkContent
          total={kpis?.serialTotal || 0}
          distinctLabel="Networks"
          distinctValue={kpis?.serialNetworks || 0}
          extraLabel="Parent Devices"
          extraValue={kpis?.serialParents || 0}
          statusData={kpis?.serialByStatus}
        />
      </DashboardTile>

      {/* Row 3: PLC Messaging placeholder */}
      <DashboardTile
        title="PLC Messaging"
        icon={MessageSquare}
        className="placeholder-tile"
      >
        <span className="placeholder-label">No data available</span>
      </DashboardTile>
    </DashboardLayout>
  );
};

export default NetworkingDashboardPage;
